import { useState, useEffect } from "react";
import { X, Plus, Trash2, Loader2, FolderTree, Pencil, Check, ArrowUpDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface WallpaperCategory {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

interface WallpaperCategoryManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const WallpaperCategoryManager = ({ isOpen, onClose }: WallpaperCategoryManagerProps) => {
  const [categories, setCategories] = useState<WallpaperCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState<WallpaperCategory | null>(null);
  const [editingCategory, setEditingCategory] = useState<WallpaperCategory | null>(null);
  const [editName, setEditName] = useState("");
  const [movingCategory, setMovingCategory] = useState<WallpaperCategory | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("wallpaper_categories")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching wallpaper categories:", error);
      toast.error("Errore nel caricamento delle categorie");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Inserisci un nome per la categoria");
      return;
    }

    setIsSaving(true);
    try {
      const slug = generateSlug(newCategoryName);
      const maxOrder = Math.max(...categories.map(c => c.sort_order || 0), 0);
      
      const { error } = await supabase.from("wallpaper_categories").insert({
        name: newCategoryName.trim(),
        slug,
        sort_order: maxOrder + 1,
      });

      if (error) throw error;

      toast.success("Categoria creata!");
      setNewCategoryName("");
      fetchCategories();
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error("Errore nella creazione della categoria");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return;

    // Don't allow deleting the "all" category
    if (deleteCategory.slug === "all") {
      toast.error("Non puoi eliminare la categoria 'Tutti'");
      setDeleteCategory(null);
      return;
    }

    try {
      const { error } = await supabase
        .from("wallpaper_categories")
        .delete()
        .eq("id", deleteCategory.id);

      if (error) throw error;

      toast.success("Categoria eliminata!");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Errore nell'eliminazione della categoria");
    } finally {
      setDeleteCategory(null);
    }
  };

  const handleStartEdit = (category: WallpaperCategory) => {
    if (category.slug === "all") {
      toast.error("Non puoi modificare la categoria 'Tutti'");
      return;
    }
    setEditingCategory(category);
    setEditName(category.name);
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editName.trim()) return;

    try {
      const { error } = await supabase
        .from("wallpaper_categories")
        .update({
          name: editName.trim(),
          slug: generateSlug(editName),
        })
        .eq("id", editingCategory.id);

      if (error) throw error;

      toast.success("Categoria aggiornata!");
      setEditingCategory(null);
      setEditName("");
      fetchCategories();
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error("Errore nell'aggiornamento della categoria");
    }
  };

  const handleStartMove = (category: WallpaperCategory) => {
    setMovingCategory(category);
    setSelectedPosition("");
  };

  const handleConfirmMove = async () => {
    if (!movingCategory || !selectedPosition) return;

    const sortedCategories = [...categories].sort((a, b) => a.sort_order - b.sort_order);
    const targetIndex = parseInt(selectedPosition);
    const currentIndex = sortedCategories.findIndex(c => c.id === movingCategory.id);

    if (targetIndex === currentIndex) {
      setMovingCategory(null);
      return;
    }

    try {
      const newOrder = [...sortedCategories];
      const [moved] = newOrder.splice(currentIndex, 1);
      newOrder.splice(targetIndex, 0, moved);

      for (let i = 0; i < newOrder.length; i++) {
        await supabase
          .from("wallpaper_categories")
          .update({ sort_order: i })
          .eq("id", newOrder[i].id);
      }

      toast.success("Posizione aggiornata!");
      fetchCategories();
    } catch (error) {
      console.error("Error moving category:", error);
      toast.error("Errore nello spostamento della categoria");
    } finally {
      setMovingCategory(null);
    }
  };

  const getPositionOptions = () => {
    const sortedCategories = [...categories].sort((a, b) => a.sort_order - b.sort_order);
    return sortedCategories.map((c, index) => ({
      value: index.toString(),
      label: `${index + 1}. ${c.name}`,
      isCurrent: movingCategory ? c.id === movingCategory.id : false
    }));
  };

  const renderCategoryItem = (category: WallpaperCategory) => {
    const isEditing = editingCategory?.id === category.id;
    const isAllCategory = category.slug === "all";

    return (
      <motion.div
        key={category.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center justify-between p-3 rounded-xl hover:bg-muted/50 transition-colors group"
      >
        <div className="flex items-center gap-2 flex-1">
          <FolderTree className="w-4 h-4 text-primary" />
          
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-8 text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveEdit();
                  if (e.key === "Escape") setEditingCategory(null);
                }}
              />
              <button
                onClick={handleSaveEdit}
                className="p-1.5 rounded-full text-green-500 hover:bg-green-500/10 transition-all"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => setEditingCategory(null)}
                className="p-1.5 rounded-full text-muted-foreground hover:bg-muted transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <span className="font-medium">{category.name}</span>
          )}
        </div>
        
        {!isEditing && !isAllCategory && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => handleStartMove(category)}
              className="p-1.5 rounded-full text-blue-500 hover:bg-blue-500/10 transition-all"
              title="Sposta"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleStartEdit(category)}
              className="p-1.5 rounded-full text-primary hover:bg-primary/10 transition-all"
              title="Modifica"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDeleteCategory(category)}
              className="p-1.5 rounded-full text-destructive hover:bg-destructive/10 transition-all"
              title="Elimina"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose} shouldScaleBackground={false}>
        <DrawerContent className="h-[85vh] bg-background border-t border-border">
          <DrawerHeader className="relative border-b border-border">
            <button
              onClick={onClose}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <DrawerTitle className="text-xl font-bold text-center">
              Gestione Categorie Sfondi
            </DrawerTitle>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Add New Category */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-2xl">
              <h3 className="text-sm font-medium text-foreground">
                Nuova Categoria
              </h3>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Nome categoria"
              />
              <Button
                onClick={handleAddCategory}
                disabled={isSaving}
                className="w-full gap-2"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Aggiungi Categoria
              </Button>
            </div>

            {/* Categories List */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground px-1">
                Categorie esistenti
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : categories.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed rounded-xl">
                  <FolderTree className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nessuna categoria creata</p>
                </div>
              ) : (
                <AnimatePresence>
                  {[...categories].sort((a, b) => a.sort_order - b.sort_order).map(renderCategoryItem)}
                </AnimatePresence>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare "{deleteCategory?.name}". Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Position Dialog */}
      <AlertDialog open={!!movingCategory} onOpenChange={() => setMovingCategory(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Sposta "{movingCategory?.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              Seleziona la nuova posizione per questa categoria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={selectedPosition} onValueChange={setSelectedPosition}>
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Seleziona posizione" />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border">
                {getPositionOptions().map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className={option.isCurrent ? "font-bold text-primary" : ""}
                  >
                    {option.label} {option.isCurrent && "(attuale)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMove}
              disabled={!selectedPosition}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Sposta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WallpaperCategoryManager;
